/**
 * Discovery Browse URL state via react-router location search.
 * Location/GPS stays out of the URL — use component or session restore helpers.
 *
 * Reads `useLocation().search` (history SSOT) and writes via `navigate(..., { replace })`
 * so view toggles stay aligned with the address bar when map portals remount.
 *
 * Wave 3: new UI writes entity/listKind/view — never intentType/intentKey/favoritesOnly.
 */

import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  DISCOVERY_SEARCH_PARAMS_DEFAULTS,
  discoverySearchParamsNeedSanitize,
  mergeDiscoverySearchParams,
  normalizeDiscoveryQuery,
  parseDiscoverySearchParams,
  sanitizeDiscoverySearchParams,
  type DiscoveryEntity,
  type DiscoveryListKind,
  type DiscoverySearchParamsState,
  type DiscoverySort,
  type DiscoveryView,
  type DiscoveryIntentType,
} from './discoverySearchParams.js';
import { clearDiscoveryNearbyContext } from './discoverySessionRestore.js';

export type UseDiscoverySearchParamsResult = {
  readonly params: DiscoverySearchParamsState;
  readonly searchParams: URLSearchParams;
  /** Merge patch; preserves unrelated keys; never writes cursor or GPS. */
  readonly replaceParams: (patch: Partial<DiscoverySearchParamsState>) => void;
  /** Merge patch via history push (replace:false) for Back-restorable steps. */
  readonly pushParams: (patch: Partial<DiscoverySearchParamsState>) => void;
  readonly setQ: (q: string) => void;
  readonly setView: (view: DiscoveryView) => void;
  readonly setEntity: (entity: DiscoveryEntity) => void;
  readonly setListKind: (listKind: DiscoveryListKind) => void;
  readonly setSort: (sort: DiscoverySort) => void;
  readonly setPlatformCategory: (platformCategory: string | null) => void;
  readonly setRadiusMeters: (radiusMeters: number | null) => void;
  /**
   * @deprecated Wave 2/3 — remaps truthy → setView('favorites'); never writes favoritesOnly.
   */
  readonly setFavoritesOnly: (favoritesOnly: boolean) => void;
  /**
   * @deprecated Wave 3 — legacy deep-link helper; maps intent→entity and does not write intent*.
   */
  readonly setIntent: (intentType: DiscoveryIntentType, intentKey: string) => void;
  readonly clearIntent: () => void;
  readonly clearCategory: () => void;
  /**
   * Reset category, radius, and sort; keep q, view, entity, listKind.
   * Also clears session nearby lat/lng so URL radius wipe cannot leave orphan GPS.
   */
  readonly clearFilters: () => void;
};

export function useDiscoverySearchParams(): UseDiscoverySearchParamsResult {
  const location = useLocation();
  const navigate = useNavigate();
  const locationRef = useRef(location);
  useEffect(() => {
    locationRef.current = location;
  }, [location]);

  const searchParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  );

  const params = useMemo(
    () => parseDiscoverySearchParams(searchParams),
    [searchParams],
  );

  const writeParams = useCallback(
    (patch: Partial<DiscoverySearchParamsState>, replace: boolean): void => {
      const currentLocation = locationRef.current;
      const current = new URLSearchParams(currentLocation.search);
      const next = mergeDiscoverySearchParams(current, patch);
      const qs = next.toString();
      navigate(
        {
          pathname: currentLocation.pathname,
          search: qs.length > 0 ? `?${qs}` : '',
        },
        { replace },
      );
    },
    [navigate],
  );

  const replaceParams = useCallback(
    (patch: Partial<DiscoverySearchParamsState>): void => {
      writeParams(patch, true);
    },
    [writeParams],
  );

  const pushParams = useCallback(
    (patch: Partial<DiscoverySearchParamsState>): void => {
      writeParams(patch, false);
    },
    [writeParams],
  );

  useEffect(() => {
    if (!discoverySearchParamsNeedSanitize(searchParams)) {
      return;
    }
    const sanitized = sanitizeDiscoverySearchParams(searchParams);
    const qs = sanitized.toString();
    if (qs === searchParams.toString()) {
      return;
    }
    navigate(
      {
        pathname: location.pathname,
        search: qs.length > 0 ? `?${qs}` : '',
      },
      { replace: true },
    );
  }, [location.pathname, navigate, searchParams]);

  const setQ = useCallback(
    (q: string): void => {
      const current = parseDiscoverySearchParams(
        new URLSearchParams(locationRef.current.search),
      );
      const normalizedQ = normalizeDiscoveryQuery(q);
      if (normalizedQ === current.q) {
        replaceParams({ q });
        return;
      }
      // D21: platformCategory survives q changes (reverse clear-on-q).
      replaceParams({ q });
    },
    [replaceParams],
  );

  const setView = useCallback(
    (view: DiscoveryView): void => {
      replaceParams({
        view,
        ...(view !== 'list' ? { listKind: DISCOVERY_SEARCH_PARAMS_DEFAULTS.listKind } : {}),
      });
    },
    [replaceParams],
  );

  const setEntity = useCallback(
    (entity: DiscoveryEntity): void => {
      pushParams({ entity });
    },
    [pushParams],
  );

  const setListKind = useCallback(
    (listKind: DiscoveryListKind): void => {
      replaceParams({ view: 'list', listKind });
    },
    [replaceParams],
  );

  const setSort = useCallback(
    (sort: DiscoverySort): void => {
      replaceParams({ sort });
    },
    [replaceParams],
  );

  const setPlatformCategory = useCallback(
    (platformCategory: string | null): void => {
      replaceParams({ platformCategory });
    },
    [replaceParams],
  );

  const setRadiusMeters = useCallback(
    (radiusMeters: number | null): void => {
      replaceParams({ radiusMeters });
    },
    [replaceParams],
  );

  const setFavoritesOnly = useCallback(
    (favoritesOnly: boolean): void => {
      if (favoritesOnly) {
        replaceParams({ view: 'favorites' });
        return;
      }
      if (params.view === 'favorites') {
        replaceParams({ view: 'list' });
        return;
      }
    },
    [params.view, replaceParams],
  );

  /**
   * Legacy: remaps intent→entity / platformCategory; never writes intent* to the URL.
   * IntentPicker UI is retired — keep for deep-link / test compatibility only.
   */
  const setIntent = useCallback(
    (intentType: DiscoveryIntentType, intentKey: string): void => {
      if (intentType === 'productService') {
        pushParams({ entity: 'product' });
        return;
      }
      if (intentType === 'tenant') {
        pushParams({ entity: 'tenant' });
        return;
      }
      pushParams({ platformCategory: intentKey });
    },
    [pushParams],
  );

  const clearIntent = useCallback((): void => {
    replaceParams({
      intentType: null,
      intentKey: null,
    });
  }, [replaceParams]);

  const clearCategory = useCallback((): void => {
    replaceParams({
      platformCategory: null,
      intentType: null,
      intentKey: null,
    });
  }, [replaceParams]);

  const clearFilters = useCallback((): void => {
    replaceParams({
      platformCategory: DISCOVERY_SEARCH_PARAMS_DEFAULTS.platformCategory,
      radiusMeters: DISCOVERY_SEARCH_PARAMS_DEFAULTS.radiusMeters,
      sort: DISCOVERY_SEARCH_PARAMS_DEFAULTS.sort,
    });
    clearDiscoveryNearbyContext();
  }, [replaceParams]);

  return {
    params,
    searchParams,
    replaceParams,
    pushParams,
    setQ,
    setView,
    setEntity,
    setListKind,
    setSort,
    setPlatformCategory,
    setRadiusMeters,
    setFavoritesOnly,
    setIntent,
    clearIntent,
    clearCategory,
    clearFilters,
  };
}
